import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Must list every tag the sanitizer in `RichTextContent` admits, and stay in
// step with the operations-side twin in `shared/text/rich-text-to-plain-text.ts`.
// A tag that renders but is not detected here is worse than one that is dropped:
// the value falls to the plain-text branch, the reader sees the literal tags,
// and `richTextEditorContent()` escapes them into the content on the next save.
//
// `b` and `i` are here for that reason. Tiptap writes `<strong>`/`<em>`, but a
// paste from Word or Google Docs leaves the legacy pair, and the sanitizer has
// always rendered them — so a note pasted from a doc printed its own `<b>` tags
// on the job page (and every other read surface) until the next save baked them
// in. `rich-text.test.ts` asserts this list against the sanitizer allowlist so
// the two cannot drift again.
//
// Single-letter alternatives are safe against the legacy `<A1>`/`<B1>`
// diagnostic tokens these regexes deliberately preserve: the tag name must be
// followed by whitespace, `/` or `>`, and a digit is none of those.
const RICH_TEXT_HTML_TAG =
  /<\/?(?:p|div|br|strong|b|em|i|u|s|strike|del|ul|ol|li|h[1-6]|blockquote|pre|code|a|span|hr)(?:\s[^<>]*?)?\s*\/?>/i;

/** Returns true when the string contains supported editor-generated markup. */
export function isHtmlContent(value: string): boolean {
  return RICH_TEXT_HTML_TAG.test(value);
}

/** snake_case / enum → "Title Case". Empty/null → "—". Shared humanizer. */
export function formatLabel(value?: string | null) {
  if (!value) return '—';
  return value
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
